import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  claimDailyCheckIn,
  claimDailyUnlockTask,
  claimEmailBindingTask,
  claimLimitedRechargeTask,
  fetchDailyCheckInStatus,
  fetchDailyUnlockTaskStatus,
  fetchAdvertisements,
  fetchEmailBindingTaskStatus,
  fetchLimitedRechargeTaskStatus,
  type DailyCheckInDay,
  type DailyCheckInStatus,
  type DailyUnlockTaskStatus,
  type EmailBindingTaskStatus,
  type LimitedRechargeTaskStatus,
  type PublicAdvertisement,
} from '@/api/member';
import {
  ensureMemberAccessToken,
  readMemberSession,
  refreshCurrentMember,
  rememberMemberLoginReturn,
  type MemberSession,
} from '@/utils/memberSession';
import CreatorCenterBanner from '../components/CreatorCenterBanner';

interface CoinParticle {
  id: number;
  x: number;
  y: number;
  delay: number;
}

interface EarnTask {
  id: string;
  titleKey: string;
  titleParams?: Record<string, number>;
  reward: number;
  icon: string;
  color: string;
}

interface TaskGroup {
  id: 'newbie' | 'daily';
  tasks: EarnTask[];
}

const fallbackRewards = [5, 5, 5, 5, 5, 5, 10];

const taskGroups: TaskGroup[] = [
  {
    id: 'newbie',
    tasks: [
      { id: 'bind-email', titleKey: 'cinema_bindEmailTaskTitle', reward: 10, icon: 'ri-mail-line', color: 'text-sky-400' },
    ],
  },
  {
    id: 'daily',
    tasks: [
      { id: 'unlock_3_episodes', titleKey: 'cinema_dailyUnlockTaskTitle', titleParams: { count: 3 }, reward: 5, icon: 'ri-lock-unlock-line', color: 'text-rose-400' },
      { id: 'unlock_5_episodes', titleKey: 'cinema_dailyUnlockTaskTitle', titleParams: { count: 5 }, reward: 12, icon: 'ri-movie-2-line', color: 'text-rose-400' },
    ],
  },
];

export default function MobileEarnPage() {
  const { t } = useTranslation('cinema');
  const navigate = useNavigate();
  const [member, setMember] = useState<MemberSession>(() => readMemberSession());
  const [status, setStatus] = useState<DailyCheckInStatus | null>(null);
  const [limitedTask, setLimitedTask] = useState<LimitedRechargeTaskStatus | null>(null);
  const [emailTask, setEmailTask] = useState<EmailBindingTaskStatus | null>(null);
  const [dailyUnlockTasks, setDailyUnlockTasks] = useState<DailyUnlockTaskStatus | null>(null);
  const [recommendations, setRecommendations] = useState<PublicAdvertisement[]>([]);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [loading, setLoading] = useState(member.isLoggedIn);
  const [claiming, setClaiming] = useState(false);
  const [claimingLimitedTask, setClaimingLimitedTask] = useState(false);
  const [claimingEmailTask, setClaimingEmailTask] = useState(false);
  const [claimingDailyTask, setClaimingDailyTask] = useState('');
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [showCoinAnimation, setShowCoinAnimation] = useState(false);
  const [coinParticles, setCoinParticles] = useState<CoinParticle[]>([]);
  const [earnedCoins, setEarnedCoins] = useState(0);
  const coinAnimationTimeout = useRef<number | null>(null);

  const loadStatus = useCallback(async () => {
    const current = readMemberSession();
    setMember(current);
    if (!current.isLoggedIn) {
      setStatus(null);
      setLimitedTask(null);
      setEmailTask(null);
      setDailyUnlockTasks(null);
      setError('');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const token = await ensureMemberAccessToken();
      if (!token) {
        setMember(readMemberSession());
        setStatus(null);
        return;
      }
      const [checkInResult, taskResult, emailTaskResult, dailyUnlockResult] = await Promise.allSettled([
        fetchDailyCheckInStatus(token),
        fetchLimitedRechargeTaskStatus(token),
        fetchEmailBindingTaskStatus(token),
        fetchDailyUnlockTaskStatus(token),
      ]);
      if (checkInResult.status === 'fulfilled') setStatus(checkInResult.value);
      if (taskResult.status === 'fulfilled') {
        setLimitedTask(taskResult.value);
        setRemainingSeconds(taskResult.value.remainingSeconds);
      }
      if (emailTaskResult.status === 'fulfilled') setEmailTask(emailTaskResult.value);
      if (dailyUnlockResult.status === 'fulfilled') setDailyUnlockTasks(dailyUnlockResult.value);
      if ([checkInResult, taskResult, emailTaskResult, dailyUnlockResult].some((result) => result.status === 'rejected')) {
        setError(t('cinema_checkInLoadFailed'));
      }
    } catch {
      setError(t('cinema_checkInLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadStatus();
    const syncSession = () => void loadStatus();
    window.addEventListener('member-session-change', syncSession);
    return () => {
      window.removeEventListener('member-session-change', syncSession);
      if (coinAnimationTimeout.current) window.clearTimeout(coinAnimationTimeout.current);
    };
  }, [loadStatus]);

  useEffect(() => {
    let cancelled = false;
    void fetchAdvertisements('earn_recommend')
      .then((items) => {
        if (!cancelled) setRecommendations(items);
      })
      .catch(() => {
        if (!cancelled) setRecommendations([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!limitedTask || limitedTask.completed || limitedTask.expired || remainingSeconds <= 0) return;
    const timer = window.setInterval(() => setRemainingSeconds((current) => Math.max(0, current - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [limitedTask, remainingSeconds]);

  const showToast = (message: string) => {
    setToastMessage(message);
    window.setTimeout(() => setToastMessage(''), 2000);
  };

  const triggerCoinAnimation = (reward: number) => {
    setEarnedCoins(reward);
    setCoinParticles(Array.from({ length: 8 }, (_, id) => ({
      id,
      x: (Math.random() - 0.5) * 120,
      y: -(40 + Math.random() * 60),
      delay: id * 0.08,
    })));
    setShowCoinAnimation(true);
    if (coinAnimationTimeout.current) window.clearTimeout(coinAnimationTimeout.current);
    coinAnimationTimeout.current = window.setTimeout(() => {
      setShowCoinAnimation(false);
      setCoinParticles([]);
    }, 1500);
  };

  const handleCheckIn = async () => {
    if (!member.isLoggedIn) {
      rememberMemberLoginReturn('/ads');
      navigate('/login');
      return;
    }
    if (status?.signedInToday || claiming) return;
    setClaiming(true);
    setError('');
    try {
      const token = await ensureMemberAccessToken();
      if (!token) {
        rememberMemberLoginReturn('/ads');
        navigate('/login');
        return;
      }
      const nextStatus = await claimDailyCheckIn(token);
      setStatus(nextStatus);
      if (nextStatus.grantedPoints > 0) {
        triggerCoinAnimation(nextStatus.grantedPoints);
        showToast(`+${nextStatus.grantedPoints} ${t('cinema_earnCoinsCoinUnit')}`);
      } else {
        showToast(t('cinema_checkInNoReward'));
      }
      void refreshCurrentMember().then(setMember).catch(() => undefined);
    } catch {
      setError(t('cinema_checkInLoadFailed'));
      showToast(t('cinema_checkInFailed'));
    } finally {
      setClaiming(false);
    }
  };

  const handleTaskAction = (taskId: string) => {
    if (taskId === 'like-5-videos') navigate('/mylist');
    else navigate('/profile');
  };

  const handleDailyUnlockTaskAction = async (taskCode: string) => {
    const task = dailyUnlockTasks?.tasks.find((item) => item.taskCode === taskCode);
    if (!task?.completed) {
      navigate('/mylist');
      return;
    }
    if (task.rewarded || claimingDailyTask) return;
    setClaimingDailyTask(taskCode);
    setError('');
    try {
      const token = await ensureMemberAccessToken();
      if (!token) {
        rememberMemberLoginReturn('/ads');
        navigate('/login');
        return;
      }
      const nextStatus = await claimDailyUnlockTask(token, taskCode);
      setDailyUnlockTasks(nextStatus);
      if (nextStatus.grantedPoints > 0) {
        triggerCoinAnimation(nextStatus.grantedPoints);
        showToast(`+${nextStatus.grantedPoints} ${t('cinema_earnCoinsCoinUnit')}`);
        setMember(await refreshCurrentMember());
      }
    } catch {
      setError(t('cinema_taskClaimFailed'));
    } finally {
      setClaimingDailyTask('');
    }
  };

  const handleEmailTaskAction = async () => {
    if (!emailTask?.completed) {
      navigate('/profile');
      return;
    }
    if (emailTask.rewarded || claimingEmailTask) return;
    setClaimingEmailTask(true);
    setError('');
    try {
      const token = await ensureMemberAccessToken();
      if (!token) {
        rememberMemberLoginReturn('/ads');
        navigate('/login');
        return;
      }
      const nextStatus = await claimEmailBindingTask(token);
      setEmailTask(nextStatus);
      if (nextStatus.grantedPoints > 0) {
        triggerCoinAnimation(nextStatus.grantedPoints);
        showToast(`+${nextStatus.grantedPoints} ${t('cinema_earnCoinsCoinUnit')}`);
        setMember(await refreshCurrentMember());
      }
    } catch {
      setError(t('cinema_taskClaimFailed'));
    } finally {
      setClaimingEmailTask(false);
    }
  };

  const openRecharge = () => {
    if (!member.isLoggedIn) {
      rememberMemberLoginReturn('/ads');
      navigate('/login');
      return;
    }
    window.dispatchEvent(new Event('cinema-open-recharge'));
  };

  const handleLimitedTaskAction = async () => {
    const isExpired = Boolean(limitedTask && !limitedTask.completed && (limitedTask.expired || remainingSeconds <= 0));
    if (isExpired) return;
    if (!limitedTask?.completed) {
      openRecharge();
      return;
    }
    if (limitedTask.rewarded || claimingLimitedTask) return;
    setClaimingLimitedTask(true);
    setError('');
    try {
      const token = await ensureMemberAccessToken();
      if (!token) {
        rememberMemberLoginReturn('/ads');
        navigate('/login');
        return;
      }
      const nextStatus = await claimLimitedRechargeTask(token);
      setLimitedTask(nextStatus);
      const reward = nextStatus.grantedPoints || nextStatus.rewardPoints;
      if (nextStatus.grantedPoints > 0) {
        triggerCoinAnimation(reward);
        showToast(`+${reward} ${t('cinema_earnCoinsCoinUnit')}`);
        setMember(await refreshCurrentMember());
      }
    } catch {
      setError(t('cinema_taskClaimFailed'));
    } finally {
      setClaimingLimitedTask(false);
    }
  };

  const days: DailyCheckInDay[] = status?.days?.length
    ? status.days
    : fallbackRewards.map((rewardPoints, index) => ({ day: index + 1, rewardPoints, claimed: false, current: index === 0 }));
  const balance = status?.pointsBalance ?? member.pointsBalance;
  const streak = status?.streakDays ?? 0;
  const todayReward = status?.todayReward ?? days.find((day) => day.current)?.rewardPoints ?? fallbackRewards[0];
  const limitedTaskExpired = Boolean(limitedTask && !limitedTask.completed && (limitedTask.expired || remainingSeconds <= 0));

  return (
    <div className="pb-20 pt-14 md:pb-24 md:pt-16">
      <div className="mx-auto max-w-2xl space-y-5 px-4 md:px-10 lg:px-14">
        <div className="relative mb-2 overflow-hidden rounded-2xl border border-background-800/60">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 via-rose-500/10 to-transparent" />
            <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-amber-400/15 blur-3xl" />
            <div className="absolute bottom-0 left-0 h-24 w-24 rounded-full bg-rose-400/10 blur-2xl" />
          </div>
          <div className="relative p-5 md:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="mb-1 text-xs text-foreground-500 md:text-sm">{t('cinema_earnCoinsBalance')}</p>
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-400/20"><i className="ri-coin-line text-lg text-amber-400" /></span>
                  <span className="font-heading text-3xl font-bold text-foreground-50 md:text-4xl">{balance}</span>
                </div>
                <p className="mt-1.5 text-xs text-foreground-500">{t('cinema_earnCoinsSignInStreak', { count: streak })}</p>
              </div>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => void handleCheckIn()}
                  disabled={claiming || Boolean(status?.signedInToday)}
                  className={`relative flex min-w-[88px] cursor-pointer items-center justify-center gap-1.5 overflow-hidden whitespace-nowrap rounded-full px-6 py-3 text-sm font-semibold transition-all duration-300 ${status?.signedInToday ? 'cursor-not-allowed bg-background-700 text-foreground-500' : 'bg-accent-500 text-background-950 hover:bg-accent-400 active:scale-95'}`}
                >
                  {(loading || claiming) && <i className="ri-loader-4-line animate-spin" />}
                  {status?.signedInToday ? t('cinema_earnCoinsCheckedIn') : t('cinema_earnCoinsCheckIn')}
                </button>
                {showCoinAnimation && coinParticles.map((particle) => (
                  <div key={particle.id} className="pointer-events-none absolute left-1/2 top-1/2 z-20" style={{ animationDelay: `${particle.delay}s` }}>
                    <div className="animate-ao-coin-fall" style={{ transform: `translate(${particle.x}px, ${particle.y}px)` }}>
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-400 shadow-[0_0_12px_rgba(255,180,60,0.8)]"><span className="text-[9px] font-bold text-foreground-950">+{earnedCoins}</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-background-800/40 bg-background-900/40 p-4 md:p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-bold text-foreground-50">{t('cinema_earnCoinsCheckIn')}</h3>
            <span className="text-xs text-foreground-500">{status?.signedInToday ? t('cinema_earnCoinsCheckedIn') : `+${todayReward} ${t('cinema_earnCoinsCoinUnit')}`}</span>
          </div>
          <div className="grid grid-cols-7 gap-2">
            {days.map((day) => {
              const isCycleBonusDay = day.day === 7;
              const dayStyle = isCycleBonusDay
                ? `border-2 border-[#FCD34D] bg-gradient-to-br from-[#FBBF24] to-[#F97316] text-white shadow-[0_0_0_3px_rgba(249,115,22,0.16),0_7px_18px_rgba(249,115,22,0.32)] ${day.current ? 'ring-2 ring-[#FDE68A]/55' : ''}`
                : day.claimed
                  ? 'border border-accent-500/30 bg-accent-500/20 text-accent-400'
                  : day.current
                    ? 'border border-amber-400/30 bg-amber-400/20 text-amber-400'
                    : 'border border-background-800/50 bg-background-800/50 text-foreground-500';
              return (
                <div key={day.day} className="flex flex-col items-center gap-1">
                  <div className={`relative flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold transition-all md:h-12 md:w-12 ${dayStyle}`}>
                    {isCycleBonusDay && <span className="absolute -right-0.5 -top-1 flex h-4 w-4 items-center justify-center rounded-full border border-[#FDE68A] bg-[#7C2D12] text-[9px] text-[#FDE68A] shadow-sm"><i className="ri-vip-crown-fill" /></span>}
                    {day.claimed ? <i className="ri-check-line text-sm" /> : <span className={`text-xs ${isCycleBonusDay ? 'font-black' : ''}`}>+{day.rewardPoints}</span>}
                  </div>
                  <span className={`text-[10px] ${isCycleBonusDay ? 'font-bold text-[#FDBA74]' : day.current ? 'text-amber-400' : 'text-foreground-600'}`}>{day.current && !day.claimed ? t('cinema_earnCoinsToday') : t('cinema_earnCoinsDayLabel', { day: day.day })}</span>
                </div>
              );
            })}
          </div>
          {error && <button type="button" onClick={() => void loadStatus()} className="mt-4 flex w-full items-center justify-center gap-1.5 text-xs text-red-400"><i className="ri-refresh-line" />{t('cinema_retry')}</button>}
        </div>

        {!limitedTaskExpired && (
        <div className="relative overflow-hidden rounded-2xl border border-[#F97316]/55 bg-gradient-to-br from-[#431407] via-[#28140F] to-[#171312] p-4 shadow-[0_10px_30px_rgba(249,115,22,0.14),inset_0_1px_0_rgba(255,255,255,0.06)] md:p-5">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#EF4444] via-[#F97316] to-[#FB923C]" />
          <div className="relative mb-4 flex flex-wrap items-center justify-between gap-2.5">
            <div className="flex items-center gap-2">
              <span className="relative flex h-6 w-6 items-center justify-center rounded-md bg-[#F97316]/20 text-[#FB923C]"><i className="ri-flashlight-fill text-sm" /><span className="absolute -right-0.5 -top-0.5 h-2 w-2 animate-pulse rounded-full border border-[#431407] bg-red-400" /></span>
              <h3 className="text-sm font-extrabold text-white">{t('cinema_earnCoinsLimitedTask')}</h3>
            </div>
            {member.isLoggedIn && !limitedTask?.completed && !limitedTaskExpired && (
              <div className="ml-auto flex items-center gap-2 rounded-lg border border-[#FB923C]/45 bg-black/30 px-2.5 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                <i className="ri-time-line text-sm text-[#FDBA74]" />
                <span className="text-[10px] font-semibold text-[#FED7AA]">{t('cinema_earnCoinsTimeLeft')}</span>
                <strong className="font-mono text-sm font-extrabold tabular-nums text-white">{limitedTask ? formatRemainingTime(remainingSeconds) : '--:--:--'}</strong>
              </div>
            )}
          </div>
          <div className="relative flex items-center gap-3 rounded-xl border border-[#F97316]/20 bg-black/25 p-3 transition-colors hover:bg-black/35 md:gap-4 md:p-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#FB923C]/20 bg-[#F97316]/15 shadow-[0_6px_16px_rgba(249,115,22,0.12)]"><i className="ri-bank-card-line text-lg text-[#FDBA74]" /></span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2"><p className="text-sm font-bold text-white">{t('cinema_limitedRechargeTaskTitle')}</p><span className="shrink-0 rounded-md bg-[#F97316]/15 px-1.5 py-0.5 text-sm font-extrabold text-[#FDBA74]">+{limitedTask?.rewardPoints ?? 30}</span></div>
            </div>
            <button type="button" onClick={() => void handleLimitedTaskAction()} disabled={Boolean(limitedTask?.rewarded || limitedTaskExpired || claimingLimitedTask)} className={`whitespace-nowrap rounded-full px-3.5 py-2 text-xs font-bold transition-all ${limitedTask?.rewarded || limitedTaskExpired || claimingLimitedTask ? 'cursor-not-allowed bg-background-700 text-foreground-600' : 'cursor-pointer bg-[#F97316] text-white shadow-[0_6px_16px_rgba(249,115,22,0.28)] hover:bg-[#FB923C] active:scale-95'}`}>{claimingLimitedTask ? t('cinema_claiming') : limitedTask?.rewarded ? t('cinema_earnCoinsTaskCompleted') : limitedTask?.completed ? t('cinema_claimReward') : limitedTaskExpired ? t('cinema_limitedRechargeTaskExpired') : t('cinema_limitedRechargeTaskAction')}</button>
          </div>
        </div>

        )}

        {taskGroups.map((group) => (
          <div key={group.id} className="rounded-2xl border border-background-800/40 bg-background-900/40 p-4 md:p-5">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="h-4 w-1 rounded-full bg-accent-500" />
                <h3 className="text-sm font-bold text-foreground-50">{group.id === 'newbie' ? t('cinema_earnCoinsNewbieTask') : t('cinema_earnCoinsDailyTask')}</h3>
              </div>
            </div>
            <div className="space-y-2">
              {group.tasks.map((task) => {
                const isEmailTask = task.id === 'bind-email';
                const isDailyUnlockTask = task.id === 'unlock_3_episodes' || task.id === 'unlock_5_episodes';
                const dailyUnlockTask = isDailyUnlockTask ? dailyUnlockTasks?.tasks.find((item) => item.taskCode === task.id) : undefined;
                const actionLabel = isDailyUnlockTask
                  ? claimingDailyTask === task.id
                    ? t('cinema_claiming')
                    : dailyUnlockTask?.rewarded
                      ? t('cinema_earnCoinsTaskCompleted')
                      : dailyUnlockTask?.completed
                        ? t('cinema_claimReward')
                        : t('cinema_unlockAction')
                  : isEmailTask
                  ? claimingEmailTask
                    ? t('cinema_claiming')
                    : emailTask?.rewarded
                      ? t('cinema_earnCoinsTaskCompleted')
                      : emailTask?.completed
                        ? t('cinema_claimReward')
                        : t('cinema_bindEmailAction')
                  : t('cinema_unlockAction');
                const targetCount = dailyUnlockTask?.targetCount ?? (task.id === 'unlock_3_episodes' ? 3 : 5);
                const progressLabel = isDailyUnlockTask
                  ? t('cinema_dailyTaskProgress', {
                      current: Math.min(dailyUnlockTasks?.unlockCount ?? 0, targetCount),
                      target: targetCount,
                    })
                  : undefined;
                const disabled = isDailyUnlockTask
                  ? Boolean(dailyUnlockTask?.rewarded || claimingDailyTask === task.id)
                  : isEmailTask && Boolean(emailTask?.rewarded || claimingEmailTask);
                const onAction = isDailyUnlockTask
                  ? () => void handleDailyUnlockTaskAction(task.id)
                  : isEmailTask
                    ? () => void handleEmailTaskAction()
                    : () => handleTaskAction(task.id);
                return <TaskRow key={task.id} task={task} actionLabel={actionLabel} progressLabel={progressLabel} disabled={disabled} onAction={onAction} />;
              })}
            </div>
          </div>
        ))}

        <CreatorCenterBanner />

        {recommendations.length > 0 && (
          <section aria-labelledby="earn-recommendations-title">
            <div className="mb-3 flex items-center gap-2 px-1">
              <span className="h-4 w-1 rounded-full bg-accent-500" />
              <h3 id="earn-recommendations-title" className="text-sm font-bold text-foreground-50">
                {t('cinema_earnCoinsRecommendations')}
              </h3>
            </div>
            <div className="space-y-2">
              {recommendations.map((advertisement) => (
                <a
                  key={advertisement.id}
                  href={advertisement.targetUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex min-h-20 items-center gap-3 rounded-xl border border-background-800/50 bg-background-900/50 p-3 transition-colors hover:border-accent-500/35 hover:bg-background-800/60"
                >
                  {advertisement.displayType === 'image' && advertisement.imageUrl ? (
                    <img
                      src={advertisement.imageUrl}
                      alt={advertisement.title}
                      loading="lazy"
                      className="h-16 w-24 shrink-0 rounded-md object-cover md:w-28"
                    />
                  ) : (
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-500/10 text-accent-400">
                      <i className="ri-links-line text-lg" />
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <strong className="block truncate text-sm text-foreground-100">{advertisement.title}</strong>
                    {advertisement.description && (
                      <span className="mt-1 block line-clamp-2 text-xs leading-5 text-foreground-500">{advertisement.description}</span>
                    )}
                  </span>
                  <i className="ri-arrow-right-s-line shrink-0 text-lg text-foreground-600 transition-transform group-hover:translate-x-0.5 group-hover:text-accent-400" />
                </a>
              ))}
            </div>
          </section>
        )}

      </div>

      {toastMessage && <div className="animate-ao-fade-in-up fixed left-1/2 top-20 z-50 -translate-x-1/2 rounded-lg border border-background-700 bg-background-900 px-4 py-2 text-sm text-foreground-200 shadow-xl">{toastMessage}</div>}
    </div>
  );
}

function TaskRow({ task, actionLabel, progressLabel, disabled = false, onAction }: { task: EarnTask; actionLabel?: string; progressLabel?: string; disabled?: boolean; onAction: () => void }) {
  const { t } = useTranslation('cinema');
  return (
    <div className="flex items-center gap-3 rounded-xl bg-background-800/30 p-3 transition-all hover:bg-background-800/50 md:gap-4 md:p-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-foreground-50/5"><i className={`${task.icon} text-lg ${task.color}`} /></span>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center justify-between gap-2"><p className="truncate text-sm font-semibold text-foreground-200">{t(task.titleKey, task.titleParams)}</p><span className="shrink-0 text-sm font-bold text-amber-400">+{task.reward}</span></div>
        <div className="mt-1 flex items-center justify-between"><span className="flex items-center gap-1 truncate text-xs text-foreground-500">{progressLabel ?? <><i className="ri-coin-line text-[10px] text-amber-400" />+{task.reward}</>}</span></div>
      </div>
      <button type="button" onClick={onAction} disabled={disabled} className={`min-w-[4.5rem] shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-center text-xs font-medium transition-all ${disabled ? 'cursor-not-allowed bg-background-700 text-foreground-600' : 'cursor-pointer bg-accent-500 text-background-950 hover:bg-accent-400 active:scale-95'}`}>{actionLabel ?? t('cinema_unlockAction')}</button>
    </div>
  );
}

function formatRemainingTime(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
}
